import React, { createRef, useState, useEffect } from 'react';
import { sprintf } from 'sprintf';
// import animateScrollTo from 'animated-scroll-to';

import { AtomicArtwork } from 'components/artworks/AtomicArtwork';
import { PaginatedGrid } from 'components/PaginatedGrid';
import { RadioGroup } from 'components/UI/RadioGroup';
import { RangeGroup } from 'components/UI/RangeGroup';
import { BrowseHeadline } from 'apps/browse/components/BrowseHeadline';
import { BrowseSidebar } from 'apps/browse/components/BrowseSidebar';

import { useNumericQueryParam, useEnumQueryParam, useArrayQueryParam } from 'hooks/useQueryParam';
import { useModalState } from 'hooks/useModalState';
import { useUpdateQueryParams } from 'hooks/useUpdateQueryParams';
import { useValidatedRangeState } from 'hooks/useValidatedRangeState';
import { useDebounce } from 'hooks/useDebounce';

import { usePaginatedResource } from 'hooks/usePaginatedResource';
import * as Types from 'types';

import { mapper, PER_PAGE, getPriceQueryParam } from './helpers';
// import { ArtworkPreviewModalContainer } from './ArtworkPreviewModalContainer'; // TODO: uncomment when modal is ready

import { NoResults } from 'apps/browse/components/NoResults';
import { CheckboxGroup } from 'apps/browse/components/CheckboxGroup';
import { Checkbox } from 'apps/browse/components/Checkbox';

import { useTags } from 'hooks/useTags';

import {
  BrowseFilters,
  ARTWORK_LIST_RESET_PARAMS,
  ARTWORK_LIST_DEFAULT_PRICE_RANGE,
} from './types';

export const BrowseArtworks: React.FC = () => {
  const { updateQueryParams, updateQueryParamsDebounced } = useUpdateQueryParams();

  const order = useEnumQueryParam('order', BrowseFilters.ORDER_TYPES, 'date');
  const page = useNumericQueryParam('page', 1);
  const type = useEnumQueryParam('type', Types.ARTIST_TYPES, 'curated');
  const medium = useEnumQueryParam('medium', BrowseFilters.MEDIUM_TYPES, 'any');
  const quality = useEnumQueryParam('quality', BrowseFilters.QUALITY_TYPES, 'any');
  const orientation = useEnumQueryParam('orientation', BrowseFilters.ORIENTATION_TYPES, 'any');
  const nft = useEnumQueryParam('nft', BrowseFilters.NFT_TYPES, null);

  const { tags, tagsSelected } = useTags('category');

  // We store price in internal state for performance reasons.
  // Using URL for range with controlled value makes UI sluggish.
  // Maybe we should unify this implementation and always use
  // state for storing value and URL as a backup solution (when
  // reloading page). We could come up with a larger, but unified
  // hook for such implementation, eg. useQueryState()
  const [price, setPrice] = useValidatedRangeState(
    getPriceQueryParam(ARTWORK_LIST_DEFAULT_PRICE_RANGE),
    {
      min: ARTWORK_LIST_DEFAULT_PRICE_RANGE[0],
      max: ARTWORK_LIST_DEFAULT_PRICE_RANGE[1],
    },
  );

  const url = useDebounce(
    mapper('/api/internal/artworks/lists', {
      type,
      order,
      medium,
      price,
      quality,
      nft,
      tags: tagsSelected,
      orientation,
    }),
    50,
  );

  const { items, isFetching, count } = usePaginatedResource(url, page, PER_PAGE);

  const contentRef = createRef<HTMLDivElement>();

  const resetFilters = () => {
    updateQueryParams(ARTWORK_LIST_RESET_PARAMS);
    setPrice(ARTWORK_LIST_DEFAULT_PRICE_RANGE);
  };

  const { isModalOpen, openModal, closeModal } = useModalState();

  return (
    <div>
      <BrowseHeadline title='Artworks' buttonTitle='Filter' onClick={openModal} />
      <div className='row'>
        <BrowseSidebar isModalMode={isModalOpen} title='Artworks' onRequestClose={closeModal}>
          <RadioGroup
            className='mb-2'
            name='artist-type'
            label='Section'
            options={BrowseFilters.LABELED_ARTIST_TYPES}
            selected={type}
            onChange={(itemId) => {
              updateQueryParams({ type: itemId, page: '1' });
            }}
          />
          <RadioGroup
            className='mb-2'
            name='order'
            label='Order By'
            options={BrowseFilters.LABELED_ORDER_TYPES}
            selected={order}
            onChange={(itemId) => {
              updateQueryParams({ order: itemId, page: '1' });
            }}
          />
          <RadioGroup
            className='mb-2'
            name='medium'
            label='Medium'
            options={BrowseFilters.LABELED_MEDIUM_TYPES}
            selected={medium}
            onChange={(itemId) => {
              updateQueryParams({
                medium: itemId === 'any' ? undefined : itemId,
                page: '1',
              });
            }}
          />
          <Checkbox
            className='mb-2'
            name='nft'
            label={'NFT Artworks'}
            selected={nft}
            option={{ id: 'true', label: 'NFTs', disabled: false }}
            onChange={(value) => {
              updateQueryParams({ nft: value === 'true' ? value : undefined, page: '1' });
            }}
          >
            <span className='d-inline-block lh-tight' style={{ fontSize: '12px', color: '#666' }}>
              {`Shows artworks that can be transferred to the blockchain after purchase. See the `}
              <a
                href={'https://www.seditionart.com/help-centre/nfts/beginner-s-nft-guide'}
                className='link'
              >
                Help Centre
              </a>
              {` for more information.`}
            </span>
          </Checkbox>
          <RangeGroup
            className='mb-2'
            name='price'
            label='Price'
            min={ARTWORK_LIST_DEFAULT_PRICE_RANGE[0]}
            max={ARTWORK_LIST_DEFAULT_PRICE_RANGE[1]}
            value={price}
            onChange={(range) => {
              updateQueryParamsDebounced({ price: range.join('-'), page: '1' });
              setPrice(range);
            }}
            prefix='$'
            infinity
          />
          <RadioGroup
            className='mb-2'
            name='quality'
            label='Quality'
            selected={quality}
            options={BrowseFilters.LABELED_QUALITY_TYPES}
            onChange={(qualityId) => {
              updateQueryParams({
                quality: qualityId === 'any' ? undefined : qualityId,
                page: '1',
              });
            }}
          />
          <RadioGroup
            className='mb-2'
            name='orientation'
            label='Orientation'
            selected={orientation}
            options={BrowseFilters.LABELED_ORIENTATION_TYPES}
            onChange={(orientationId) => {
              updateQueryParams({
                orientation: orientationId === 'any' ? undefined : orientationId,
                page: '1',
              });
            }}
          />
          <CheckboxGroup
            className='mb-2'
            name='category'
            label='Categories'
            selected={tagsSelected}
            options={
              tags?.map(({ name, count }) => ({
                id: name,
                label: name,
                disabled: tagsSelected.includes(name) ? false : !count,
              })) || []
            }
            onChange={(tags) => {
              updateQueryParams({ tags, page: '1' });
            }}
          />
        </BrowseSidebar>
        <div className='col' ref={contentRef}>
          {!isFetching && items.length === 0 ? <NoResults onReset={resetFilters} /> : null}
          <PaginatedGrid
            currentPage={page}
            items={items}
            count={Math.ceil(count / PER_PAGE)}
            renderItem={(item, index) => (
              <AtomicArtwork
                className='col-12 col-md-6 col-std-4 mb-2'
                key={index}
                artworkId={item.id}
                cacheKey={item.cacheKey}
              />
            )}
            onPageChange={(page) => {
              updateQueryParams({ page: String(page + 1) });
              if (contentRef.current) {
                const { top } = contentRef.current.getBoundingClientRect();

                if (top + window.scrollY < window.scrollY) {
                  window.scrollTo(0, 0);
                }
              }
            }}
          />
        </div>
      </div>
    </div>
  );
};
